// View transforms — pure, DOM-free, so they can be unit-tested and shared.
//
// The chart offers a few "views" of a value vector, chosen by frequency:
//   level  raw values
//   pop    period-over-period % change (QoQ / MoM / % change) — lag 1
//   yoy    year-over-year % change — lag = periods per year
//   index  rebased to 100 at the first usable value in the visible window
// Semi-annual series get pop and yoy too (lag 2 periods = one year).
// A series in percent (a rate, a yield) changes by DIFFERENCES instead, in
// percentage points: unemployment from 3.0 to 2.3 % is -0.7 pp, not -23 %.
// This mirrors the server-side ds_apply_transform in api/discover.R, so the
// static chart image and the live chart agree number-for-number.

import { t } from "./locale";

export type Transform = "level" | "pop" | "yoy" | "index";

/** Periods per year for a frequency — the YoY lag. null for daily/unknown. */
export function freqLag(freq: string | null | undefined): number | null {
  return { monthly: 12, quarterly: 4, "semi-annual": 2, annual: 1, weekly: 52 }[freq ?? ""] ?? null;
}

// Half a period in seconds. A value timestamped at the period START (a quarter
// dated 2026-01-01) is PLOTTED at the period midpoint so it reads as "the
// quarter", not the instant. Display-only — the stored dates/CSV stay raw.
// daily/unknown (no freqLag) -> 0 (no shift, default time ticks).
export function periodMidShiftSec(freq: string | null | undefined): number {
  const n = freqLag(freq);
  return n ? (365.25 * 86400) / (2 * n) : 0;
}

/** Label for the period-over-period button, which depends on the frequency. */
export const popLabel = (f: string | null | undefined, pp = false): string => pp
  ? t(f === "quarterly" ? "QoQ pp" : f === "monthly" ? "MoM pp" : "change, pp")
  : t(f === "quarterly" ? "QoQ %" : f === "monthly" ? "MoM %" : "% change");

/** A frequency code as display text. A closed vocabulary of six values, so it
 * is chrome (translated here) rather than data — the pipeline emits the code.
 * Spelled out rather than t(f): the keys stay greppable, so chrome-coverage can
 * see them, and an unknown code falls through as itself instead of vanishing. */
export const freqLabel = (f: string | null | undefined): string => {
  switch (f) {
    case "daily": return t("daily");
    case "weekly": return t("weekly");
    case "monthly": return t("monthly");
    case "quarterly": return t("quarterly");
    case "semi-annual": return t("semi-annual");
    case "annual": return t("annual");
    default: return f || "";
  }
};

// The transform buttons available for a frequency: sub-annual series (semi-annual
// included) get a
// period-over-period rate PLUS year-on-year; annual just has year-on-year;
// weekly/daily/unknown get period-over-period only. Level and Index always.
// `pp`: the series is in percent, so changes are in percentage points.
export function transformOptions(freq: string | null | undefined, pp = false): [Transform, string][] {
  const o: [Transform, string][] = [["level", t("Level")]];
  const yoy = pp ? t("YoY pp") : t("YoY %");
  if (freq === "quarterly" || freq === "monthly" || freq === "semi-annual") o.push(["pop", popLabel(freq, pp)], ["yoy", yoy]);
  else if (freq === "annual") o.push(["yoy", yoy]);
  else o.push(["pop", popLabel(freq, pp)]);
  o.push(["index", t("Index")]);
  return o;
}

/** The view a dataset opens on: the datasheet's declared transform when its
 *  frequency offers it, else the level. */
export function defaultTransform(freq: string | null | undefined, declared: string | null | undefined): string {
  return declared && transformOptions(freq).some(([v]) => v === declared) ? declared : "level";
}

export interface TransformOpts {
  transform: string;
  freq: string | null | undefined;
  /** First sorted-date index visible in the window — the index rebase point. */
  indexStartIdx?: number;
  /** Set for a series in percent: pop/yoy are differences times this factor
   *  (1 for percent values, 100 for 0-1 shares), in percentage points. */
  diff?: number;
  /** ISO dates of `vals`. With them, pop/yoy compare against the observation
   *  dated one period / one year earlier, so a missing period yields null
   *  instead of comparing the wrong dates. */
  dates?: string[];
}

// Calendar step of one period: months for monthly..annual, days for weekly.
// Daily/unknown: none, so pop compares with the previous observation (trading
// days have gaps by nature).
const PERIOD_STEP: Record<string, { months?: number; days?: number }> = {
  monthly: { months: 1 }, quarterly: { months: 3 }, "semi-annual": { months: 6 },
  annual: { months: 12 }, weekly: { days: 7 },
};

/** The ISO date `periods` periods before `iso` (one year for yoy), or null when
 *  the frequency has no calendar step. Mirrors ds_shift_back in api/discover.R. */
export function shiftBack(iso: string, freq: string | null | undefined, transform: string): string | null {
  const step = PERIOD_STEP[freq ?? ""];
  if (!step) return null;
  const [y = NaN, m = NaN, d = NaN] = iso.split("-").map(Number);
  if (step.days) {
    const back = transform === "yoy" ? 52 * step.days : step.days;
    return new Date(Date.UTC(y, m - 1, d) - back * 86400000).toISOString().slice(0, 10);
  }
  const k = (y * 12 + (m - 1)) - (transform === "yoy" ? 12 : step.months as number);
  const mm = String((k % 12) + 1).padStart(2, "0");
  return `${Math.floor(k / 12)}-${mm}-${String(d).padStart(2, "0")}`;
}

// Apply the current transform to a value vector.
//   index: 100 at the first non-null AND non-zero value at/after indexStartIdx
//          (a zero base would divide to Infinity); fall back to the whole series
//          if the visible window has no usable value.
//   pop/yoy: (x / prev - 1) * 100, null where either endpoint is missing. prev is
//          the value dated one period / one year earlier when `dates` are given,
//          else the value `lag` slots back.
// Negative and zero-crossing series: a ratio against a base <= 0 flips the sign
// of the change (-100 -> -90 reads as -10 %, though the value rose) or means
// nothing at all. So a relative view needs a POSITIVE base: pop/yoy are null
// where prev <= 0, and the index is null throughout when its base (the first
// non-zero value) is negative. Percent series (`diff`) are differences and need
// no such guard. Level view is the way to read such a series. Mirrors
// ds_apply_transform in api/discover.R.
export function applyTransform(vals: (number | null)[], opts: TransformOpts): (number | null)[] {
  const { transform, freq } = opts;
  if (transform === "index") {
    const start = opts.indexStartIdx ?? 0;
    let base: number | null = null;
    for (let i = start; i < vals.length; i++) {
      const v = vals[i];
      if (v != null && v !== 0) { base = v; break; }
    }
    if (base == null) base = vals.find(x => x != null && x !== 0) ?? null;
    return vals.map(x => (x == null || base == null || base < 0 ? null : (x / base) * 100));
  }
  if (transform === "pop" || transform === "yoy") {
    const lag = transform === "pop" ? 1 : (freqLag(freq) || 1);
    const dates = opts.dates;
    const byDate = dates && PERIOD_STEP[freq ?? ""]
      ? new Map(dates.map((d, i) => [d, vals[i]] as const)) : null;
    return vals.map((x, i) => {
      let prev: number | null | undefined;
      if (byDate && dates) {
        const back = shiftBack(dates[i] ?? "", freq, transform);
        prev = back == null ? null : byDate.get(back);
      } else {
        if (i < lag) return null;
        prev = vals[i - lag];
      }
      if (prev == null || x == null) return null;
      if (opts.diff) return (x - prev) * opts.diff;
      return prev > 0 ? (x / prev - 1) * 100 : null;
    });
  }
  return vals;
}

export interface LineOpts {
  transform: string;
  /** The line's OWN frequency (an overlay may differ from the base dataset). */
  freq: string | null | undefined;
  /** ISO date of the visible window's left edge; index rebases at the line's
   *  first value on or after it. Omitted = the line's first value. */
  windowStart?: string | null;
  /** See TransformOpts.diff. */
  diff?: number;
}

/** One line's transformed values on the chart's shared date axis. The axis is
 * the union of every line's dates, so it is only dense for the densest line.
 * Transforming on the axis took YoY `lag` slots back on it, which for an annual
 * or quarterly line under a monthly overlay compared the wrong periods or found
 * nothing (the line vanished). The transform runs on the line's own sorted
 * observations with its own frequency, then lands on the axis. Same as the
 * server's chart renderer (ds_apply_transform per series, api/discover.R). */
export function transformLine(
  axis: string[], line: Map<string, number | null>, o: LineOpts,
): (number | null)[] {
  const own = [...line.keys()].sort();
  let start = 0;
  if (o.transform === "index" && o.windowStart) {
    start = own.findIndex(d => d >= (o.windowStart as string));
    if (start < 0) start = 0;
  }
  const vals = applyTransform(own.map(d => line.get(d) ?? null),
    { transform: o.transform, freq: o.freq, indexStartIdx: start, diff: o.diff, dates: own });
  const out = new Map<string, number | null>();
  own.forEach((d, i) => out.set(d, vals[i] ?? null));
  return axis.map(d => (out.has(d) ? out.get(d) ?? null : null));
}

export interface UnitOpts {
  transform: string;
  freq: string | null | undefined;
  /** Ratio-of-GDP levels rendered as a percentage axis. */
  pctMode?: boolean;
  /** The dataset's own unit label, for the level view. */
  unit?: string;
  /** The series is in percent: changes are in percentage points. */
  pp?: boolean;
}

/** The y-axis unit caption for the current view. */
export function transformUnit(o: UnitOpts): string {
  if (o.pp && o.transform === "yoy") return t("year-on-year change, percentage points");
  if (o.pp && o.transform === "pop")
    return t(o.freq === "quarterly" ? "quarter-on-quarter change, percentage points"
      : o.freq === "monthly" ? "month-on-month change, percentage points"
      : "change vs previous period, percentage points");
  if (o.transform === "yoy") return t("year-on-year change, %");
  if (o.transform === "pop")
    return t(o.freq === "quarterly" ? "quarter-on-quarter change, %"
      : o.freq === "monthly" ? "month-on-month change, %"
      : "change vs previous period, %");
  if (o.transform === "index") return t("index, viewport start = 100");
  if (o.pctMode) return t("share of GDP, %");
  return o.unit || t("level");   // o.unit is data-derived — already localized
}
